import * as z from 'zod/mini'
import { DateTime } from 'luxon'
import {
  BOOKING_HORIZON_MONTHS,
  CreateReservationBlueprintBody,
  getDateInTimezone,
  keyedIssue,
  refineWithKey,
  ManagedReservationBlueprint,
  reservationBlueprintFields,
  TimeInterval,
  WeekDay,
} from '@seiri/shared/common'
import { assertFormNumber, nonEmpty, requiredFormNumber } from './form-schema'

/** The select's "assign none" entry; the body gets an empty list for it. */
export const NONE_OPTION = 'none'

const minutesOfDay = (date: Date) => date.getHours() * 60 + date.getMinutes()

/** One row of the time grid: a time range on the picked weekdays (one API interval per weekday). */
const intervalFormModel = z
  .object({
    startTime: z.date(),
    endTime: z.date(),
    weekDays: z.array(z.enum(WeekDay)).check(nonEmpty('validation.weekdaysRequired')),
  })
  .check(
    z.superRefine((value, ctx) => {
      if (minutesOfDay(value.endTime) <= minutesOfDay(value.startTime)) {
        ctx.addIssue(keyedIssue('validation.endAfterStart', ['endTime']))
      }
    }),
  )
export type ReservationBlueprintIntervalFormModel = z.infer<typeof intervalFormModel>

/**
 * What the add and edit reservation-blueprint forms edit. It differs from the body in the pickers'
 * `Date`s, the grouped weekday rows, the "assign none" option and the price input that may be empty -
 * hence its own schema and `toReservationBlueprintBody`. The field rules are the API's
 * (`reservationBlueprintFields`); the validity window is whole days, so it may start and end on the
 * same day.
 */
export const reservationBlueprintFormModel = z
  .object({
    name: reservationBlueprintFields.name,
    description: reservationBlueprintFields.description,
    activityId: z.string().check(refineWithKey('validation.activityRequired', (value: string) => value.length > 0)),
    placeIds: z.array(z.string()).check(nonEmpty('validation.placesRequired')),
    employeeIds: z.array(z.string()).check(nonEmpty('validation.employeesRequired')),
    start: z.date(),
    end: z.date(),
    duration: reservationBlueprintFields.duration,
    durationAfter: reservationBlueprintFields.durationAfter,
    cancellationDeadline: reservationBlueprintFields.cancellationDeadline,
    /** "Klient môže pridať poznámku": the booking widget offers a note for the premise. */
    allowCustomerNote: reservationBlueprintFields.allowCustomerNote,
    intervals: z.array(intervalFormModel).check(nonEmpty('validation.intervalsRequired')),
    price: requiredFormNumber(reservationBlueprintFields.price),
  })
  .check(
    z.superRefine((value, ctx) => {
      if (DateTime.fromJSDate(value.end).startOf('day') < DateTime.fromJSDate(value.start).startOf('day')) {
        ctx.addIssue(keyedIssue('validation.endAfterStart', ['end']))
      }
    }),
  )
export type ReservationBlueprintFormModel = z.infer<typeof reservationBlueprintFormModel>

/**
 * Whether "Platí do" goes past the booking horizon (`BOOKING_HORIZON_MONTHS` ahead), by whole days.
 * The stored end day of an edited service is always allowed, so one planned further ahead before the
 * cap can still be saved. The picker's day is compared in the browser's zone, at worst a day off the
 * premise's; `ReservationBlueprintService` decides in the premise's zone.
 */
export const isBeyondPlanningHorizon = (end: Date, storedEnd: Date | null = null, now = new Date()): boolean => {
  const day = DateTime.fromJSDate(end).startOf('day')
  if (storedEnd && day.hasSame(DateTime.fromJSDate(storedEnd), 'day')) return false
  return day > DateTime.fromJSDate(now).plus({ months: BOOKING_HORIZON_MONTHS }).startOf('day')
}

/** A new row: 08:00-17:00 today, no weekday picked yet. */
export const emptyReservationBlueprintInterval = (): ReservationBlueprintIntervalFormModel => ({
  startTime: DateTime.now().startOf('day').plus({ hours: 8 }).toJSDate(),
  endTime: DateTime.now().startOf('day').plus({ hours: 17 }).toJSDate(),
  weekDays: [],
})

export const emptyReservationBlueprintFormModel = (): ReservationBlueprintFormModel => ({
  name: '',
  description: '',
  activityId: '',
  placeIds: [],
  employeeIds: [],
  start: DateTime.now().startOf('hour').plus({ hours: 1 }).toJSDate(),
  end: DateTime.now().startOf('hour').plus({ hours: 2 }).toJSDate(),
  duration: 60,
  durationAfter: 0,
  cancellationDeadline: 0,
  allowCustomerNote: false,
  intervals: [emptyReservationBlueprintInterval()],
  price: 0,
})

const parseTime = (time: string) => {
  const withSeconds = DateTime.fromFormat(time, 'HH:mm:ss')
  return withSeconds.isValid ? withSeconds : DateTime.fromFormat(time, 'HH:mm')
}

/** The stored intervals are one per weekday; the form groups equal time ranges into one row. */
export const toIntervalRows = (intervals: TimeInterval[]): ReservationBlueprintIntervalFormModel[] => {
  const rows = new Map<string, ReservationBlueprintIntervalFormModel>()
  for (const interval of intervals) {
    const key = `${interval.startTime}-${interval.endTime}`
    const row = rows.get(key)
    if (row) {
      row.weekDays.push(interval.weekDay)
    } else {
      rows.set(key, {
        startTime: parseTime(interval.startTime).toJSDate(),
        endTime: parseTime(interval.endTime).toJSDate(),
        weekDays: [interval.weekDay],
      })
    }
  }
  return Array.from(rows.values())
}

export const toReservationBlueprintFormModel = (
  blueprint: ManagedReservationBlueprint,
): ReservationBlueprintFormModel => ({
  name: blueprint.name,
  description: blueprint.description ?? '',
  activityId: blueprint.activity?.id ?? '',
  placeIds: blueprint.places.length ? blueprint.places.map((place) => place.id) : [NONE_OPTION],
  employeeIds: blueprint.assignedEmployees.length
    ? blueprint.assignedEmployees.map((employee) => employee.id)
    : [NONE_OPTION],
  start: new Date(blueprint.start),
  end: new Date(blueprint.end),
  duration: blueprint.duration,
  durationAfter: blueprint.durationAfter,
  cancellationDeadline: blueprint.cancellationDeadline,
  // `?? false`: an API older than this web build sends no setting, and an invalid form would block every save.
  allowCustomerNote: blueprint.allowCustomerNote ?? false,
  intervals: toIntervalRows(blueprint.intervals),
  price: blueprint.price || 0,
})

const withoutNone = (ids: string[]) => (ids.includes(NONE_OPTION) ? [] : ids)

/**
 * The form → the body. The validity window is whole days in the **premise's** timezone; the time
 * rows become one `HH:mm` interval per picked weekday. `description` is always sent, so `''` clears it.
 */
export const toReservationBlueprintBody = (
  model: ReservationBlueprintFormModel,
  timezone: string,
): CreateReservationBlueprintBody => ({
  name: model.name,
  description: model.description,
  activityId: model.activityId,
  placeIds: withoutNone(model.placeIds),
  employeeIds: withoutNone(model.employeeIds),
  intervals: model.intervals.flatMap(({ startTime, endTime, weekDays }) =>
    weekDays.map((weekDay) => ({
      weekDay,
      startTime: DateTime.fromJSDate(startTime).toFormat('HH:mm'),
      endTime: DateTime.fromJSDate(endTime).toFormat('HH:mm'),
    })),
  ),
  duration: model.duration,
  start: getDateInTimezone(model.start, timezone, 'start').toISOString(),
  end: getDateInTimezone(model.end, timezone, 'end').toISOString(),
  price: assertFormNumber(model.price, 'price'),
  cancellationDeadline: model.cancellationDeadline,
  allowCustomerNote: model.allowCustomerNote,
  ...(model.durationAfter > 0 && { durationAfter: model.durationAfter }),
})
